import React, { useEffect, useRef, useState } from "react";
import {
    FiAlertCircle,
    FiCheckCircle,
} from "react-icons/fi";

interface UsernameAvailabilityFieldProps {
    username: string;
    setUsername: (value: string) => void;
    getUsernameChecking: (username: string) => Promise<any>;
    label?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
}

const UsernameAvailabilityField = ({
    username,
    setUsername,
    getUsernameChecking,
    label = "Username",
    placeholder = "Enter Username",
    required = true,
    disabled = false,
    readOnly = false,
}: UsernameAvailabilityFieldProps) => {
    const [isUsernameValid, setIsUsernameValid] = useState(true);
    const [isUsernameExists, setIsUsernameExists] = useState<boolean | null>(null);
    const [isChecking, setIsChecking] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const hasUserChangedUsername = useRef(false);

    useEffect(() => {
        if (!hasUserChangedUsername.current) {
            return;
        }

        if (!username?.trim()) {
            setIsUsernameValid(true);
            setIsUsernameExists(null);
            setIsChecking(false);
            return;
        }

        const timer = setTimeout(() => {
            const value = username.trim();

            const isValid = /^[a-zA-Z0-9._-]+$/.test(value);

            setIsUsernameValid(isValid);

            if (!isValid) {
                setIsUsernameExists(null);
                return;
            }

            checkUsernameAvailability(value);
        }, 1000);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [username]);

    const checkUsernameAvailability = async (value: string) => {
        try {
            setIsChecking(true);

            const response = await getUsernameChecking(value);

            const exists =
                response?.data?.exists ??
                response?.exists ??
                false;

            setIsUsernameExists(exists);

            if (inputRef.current) {
                inputRef.current.setCustomValidity(
                    exists ? "Username already exists" : ""
                );
            }

            if (!exists) {
                setTimeout(() => {
                    setIsUsernameExists(null);
                }, 3000);
            }

        } catch (error) {
            console.error("Error checking username availability:", error);

            setIsUsernameExists(null);

            if (inputRef.current) {
                inputRef.current.setCustomValidity("");
            }
        } finally {
            setIsChecking(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        hasUserChangedUsername.current = true;

        setUsername(value);
        setIsUsernameValid(true);
        setIsUsernameExists(null);

        e.target.setCustomValidity("");
    };

    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                {label}
                {required && (
                    <span className="text-red-500"> *</span>
                )}
            </label>

            <input
                ref={inputRef}
                type="text"
                value={username}
                placeholder={placeholder}
                onChange={handleChange}
                disabled={disabled}
                readOnly={readOnly}
                required={required}
                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
            />

            {!isUsernameValid && (
                <div className="flex items-center text-xs text-red-500 mt-1">
                    <FiAlertCircle
                        className="mr-1"
                        size={16}
                    />
                    <span>
                        Please enter a valid username
                    </span>
                </div>
            )}

            {isChecking && isUsernameValid && (
                <p className="text-xs text-gray-500 mt-1">
                    Checking username...
                </p>
            )}

            {isUsernameValid &&
                isUsernameExists === false &&
                !isChecking && (
                    <div className="flex items-center text-xs text-green-600 mt-1">
                        <FiCheckCircle
                            className="mr-1"
                            size={16}
                        />
                        <span>Username is available</span>
                    </div>
                )}

            {isUsernameValid &&
                isUsernameExists === true &&
                !isChecking && (
                    <div className="flex items-center text-xs text-red-500 mt-1">
                        <FiAlertCircle
                            className="mr-1"
                            size={16}
                        />
                        <span>Username already exists</span>
                    </div>
                )}
        </div>
    );
};

export default UsernameAvailabilityField;